/**
 * Map common auth (Better Auth) error strings to customer-friendly messages.
 *
 * Better Auth surfaces server validation as `Error.message` text — sometimes
 * fine ("Invalid email or password"), sometimes raw code-y phrasing the user
 * shouldn't see ("FAILED_TO_CREATE_USER"). This wraps the existing
 * `isNetworkOrTimeoutError` pattern so callers don't have to repeat the
 * normalize logic in every catch block.
 *
 * Pass the raw caught value plus a `fallback` (what the page would have shown
 * before). Returns the friendliest message we can produce.
 */
import { isNetworkOrTimeoutError } from './networkError';

interface ErrorMap {
  /** Lowercase substring match against the original message */
  pattern: string | RegExp;
  message: string;
}

const AUTH_ERROR_MAP: ErrorMap[] = [
  {
    pattern: /user.*(exists|already)|email.*(exists|already|in use|taken)/i,
    message: 'An account with this email already exists. Try signing in instead.',
  },
  {
    pattern: /invalid.*(email|credentials|password)|incorrect.*password|unauthorized/i,
    message: 'Email or password is incorrect.',
  },
  {
    pattern: /(password.*too short|min.*length|password.*at least)/i,
    message: 'Password must be at least 8 characters.',
  },
  {
    pattern: /(rate limit|too many requests|429)/i,
    message: 'Too many attempts. Wait a moment and try again.',
  },
  {
    pattern: /(token.*expired|link.*expired|reset.*expired)/i,
    message: 'This link has expired. Request a new one.',
  },
  {
    pattern: /(token.*invalid|invalid.*token|bad.*token)/i,
    message: 'This link is invalid. Request a new one.',
  },
  {
    pattern: /(verification|verify.*email|not.*verified)/i,
    message: 'Please verify your email address before signing in.',
  },
  {
    pattern: /(account.*disabled|account.*locked|account.*suspended)/i,
    message: 'This account is disabled. Contact support.',
  },
];

export interface HumanizeOptions {
  /** Override the default network-error message — e.g. LoginPage uses
   *  "Server isn't ready yet — still waking up..." because its backend
   *  cold-start path is the most likely cause. */
  networkMessage?: string;
}

export function humanizeAuthError(
  err: unknown,
  fallback: string,
  opts: HumanizeOptions = {},
): string {
  if (isNetworkOrTimeoutError(err)) {
    return opts.networkMessage ?? 'Connection failed — please try again.';
  }
  const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
  if (!raw) return fallback;

  for (const { pattern, message } of AUTH_ERROR_MAP) {
    const matches = typeof pattern === 'string' ? raw.toLowerCase().includes(pattern) : pattern.test(raw);
    if (matches) return message;
  }
  return raw || fallback;
}
